import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash,
  Edit,
  CheckCircle,
  Clock,
  Loader,
  X,
  AlertCircle,
  PlusCircle,
  Save,
  MessageSquare,
  MailOpen,
  Download,
  Users,
  UserCheck,
} from "lucide-react";
import contactService from "@/services/api/contact.service";

import ContactReplyForm from "./SupportReplyForm";
import ContactExport from "./ContactExport";

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    new: "bg-blue-100 text-blue-800",
    "in-progress": "bg-yellow-100 text-yellow-800",
    "waiting-user": "bg-orange-100 text-orange-800",
    "waiting-admin": "bg-purple-100 text-purple-800",
    replied: "bg-purple-100 text-purple-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-100 text-gray-800",
    spam: "bg-red-100 text-red-800",
  };

  const statusIcons = {
    new: <AlertCircle size={14} className="mr-1" />,
    "in-progress": <Loader size={14} className="mr-1 animate-spin" />,
    "waiting-user": <Clock size={14} className="mr-1" />,
    "waiting-admin": <MailOpen size={14} className="mr-1" />,
    replied: <MailOpen size={14} className="mr-1" />,
    resolved: <CheckCircle size={14} className="mr-1" />,
    closed: <X size={14} className="mr-1" />,
    spam: <Trash size={14} className="mr-1" />,
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusStyles[status] || "bg-gray-100 text-gray-800"
        }`}
    >
      {statusIcons[status] || <AlertCircle size={14} className="mr-1" />}
      {status
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")}
    </span>
  );
};

// User type badge component
const UserTypeBadge = ({ userId }) => {
  const isAnonymous = !userId;

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isAnonymous
          ? "bg-orange-100 text-orange-800"
          : "bg-green-100 text-green-800"
        }`}
    >
      {isAnonymous ? (
        <Users size={12} className="mr-1" />
      ) : (
        <UserCheck size={12} className="mr-1" />
      )}
      {isAnonymous ? "Anonymous" : "Registered"}
    </span>
  );
};

export default function AdminContact() {
  // State for contacts data
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 10,
    total: 0,
  });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // State for filters
  const [filters, setFilters] = useState({
    status: "",
    email: "",
    userType: "",
    sortField: "createdAt",
    sortOrder: "desc",
  });

  // State for selected contact
  const [selectedContact, setSelectedContact] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    status: "",
    adminNotes: "",
    assignedTo: "",
  });
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success", // or "error"
  });
  // State for reply functionality
  const [isReplyMode, setIsReplyMode] = useState(false);

  // State for loading indicators
  const [isLoading, setIsLoading] = useState(false);
  const [isContactLoading, setIsContactLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Status options for dropdown
  const statusOptions = [
    "new",
    "in-progress",
    "waiting-user",
    "waiting-admin",
    "replied",
    "resolved",
    "closed",
    "spam",
  ];

  // Fetch contacts on component mount and when filters/pagination change
  useEffect(() => {
    fetchContacts();
  }, [pagination.currentPage, pagination.limit, filters]);

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      const response = await contactService.getAllSupport(
        pagination.currentPage,
        pagination.limit,
        filters
      );

      if (response.success) {
        setContacts(response.data.docs);
        setPagination({
          currentPage: response.data.page,
          totalPages: response.data.totalPages,
          limit: response.data.limit,
          total: response.data.totalDocs,
        });
      }
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContactDetails = async (id) => {
    setIsContactLoading(true);
    try {
      const response = await contactService.getSupportById(id);
      if (response.success) {
        setSelectedContact(response.data);
        setEditForm({
          status: response.data.status,
          adminNotes: response.data.adminNotes || "",
          assignedTo: response.data.assignedTo || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch contact details:", error);
    } finally {
      setIsContactLoading(false);
    }
  };

  const handleViewContact = (id) => {
    fetchContactDetails(id);
    setIsEditMode(false);
    setIsReplyMode(false);
  };

  const handleEditContact = () => {
    setIsEditMode(true);
    setIsReplyMode(false);
  };

  const handleReplyToContact = () => {
    setIsReplyMode(true);
    setIsEditMode(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value,
    });
  };

  const handleSaveContact = async () => {
    setIsUpdating(true);
    try {
      // First update the status if it has changed
      if (editForm.status !== selectedContact.status) {
        await contactService.updateTicketStatus(
          selectedContact._id,
          editForm.status
        );

        // Show success notification
        setNotification({
          show: true,
          message: `Status updated to ${editForm.status} successfully!`,
          type: "success",
        });

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setNotification({ ...notification, show: false });
        }, 5000);
      }

      // Then update the rest of the contact info if needed
      // Remove the updateSupportTicket call since it's not in your backend
      // Just fetch the updated contact details
      const updatedContact = await contactService.getSupportById(
        selectedContact._id
      );

      if (updatedContact.success) {
        setSelectedContact(updatedContact.data);
        setIsEditMode(false);
        // Update the contact in the list
        setContacts(
          contacts.map((contact) =>
            contact._id === updatedContact.data._id
              ? updatedContact.data
              : contact
          )
        );
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to update contact. Please try again.",
        type: "error",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReplySuccess = (updatedContact) => {
    // Update the selected contact with the new reply
    setSelectedContact((prev) => ({
      ...prev,
      replies: updatedContact.replies,
      lastActivity: updatedContact.lastActivity,
      status: updatedContact.status || prev.status,
      updatedAt: updatedContact.updatedAt,
    }));

    // Update the contact in the list
    setContacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact._id === updatedContact._id
          ? {
            ...contact,
            replies: updatedContact.replies,
            lastActivity: updatedContact.lastActivity,
            status: updatedContact.status || contact.status,
          }
          : contact
      )
    );

    setIsReplyMode(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
    // Reset to first page when applying new filters
    setPagination({
      ...pagination,
      currentPage: 1,
    });
  };

  const handleSortChange = (field) => {
    setFilters({
      ...filters,
      sortField: field,
      sortOrder:
        filters.sortField === field && filters.sortOrder === "asc"
          ? "desc"
          : "asc",
    });
  };

  const changePage = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination({
        ...pagination,
        currentPage: newPage,
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const closeContactDetails = () => {
    setSelectedContact(null);
    setIsEditMode(false);
    setIsReplyMode(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      status: "",
      email: "",
      userType: "",
      sortField: "createdAt",
      sortOrder: "desc",
    });
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`${
              notification.type === "success"
                ? "bg-green-50 border-green-400 text-green-700"
                : "bg-red-50 border-red-400 text-red-700"
              } border px-4 py-3 rounded relative`}
            role="alert"
          >
            <span className="block sm:inline">{notification.message}</span>
            <span
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={() => setNotification({ ...notification, show: false })}
            >
              <svg
                className={`fill-current h-6 w-6 ${
                  notification.type === "success"
                    ? "text-green-500"
                    : "text-red-500"
                  }`}
                role="button"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Support Management
          </h1>
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Download size={16} className="mr-2" />
            Export Contacts
          </button>
        </div>

        {/* Active filters display */}
        {(filters.status || filters.email || filters.userType) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-blue-800">
                Active Filters:
              </span>
              {filters.status && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Status: {filters.status}
                  <button
                    onClick={() =>
                      handleFilterChange({
                        target: { name: "status", value: "" },
                      })
                    }
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.email && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Email: {filters.email}
                  <button
                    onClick={() =>
                      handleFilterChange({
                        target: { name: "email", value: "" },
                      })
                    }
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.userType && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  User Type:{" "}
                  {filters.userType === "anonymous"
                    ? "Anonymous"
                    : "Registered"}
                  <button
                    onClick={() =>
                      handleFilterChange({
                        target: { name: "userType", value: "" },
                      })
                    }
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Contact List */}
        <div className="bg-white shadow overflow-hidden rounded-lg">
          {isLoading ? (
            <div className="flex justify-center items-center p-8">
              <Loader className="animate-spin text-indigo-500" size={36} />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              No support tickets found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortChange("name")}
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortChange("email")}
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortChange("mobile")}
                    >
                      Mobile No
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortChange("subject")}
                    >
                      Subject
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      User Type
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortChange("status")}
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortChange("createdAt")}
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {contact.userId?.fullName || contact.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.userId?.email || contact.email || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.userId?.mobileNo || contact.mobile || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.subject.length > 30
                          ? `${contact.subject.substring(0, 30)}...`
                          : contact.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <UserTypeBadge userId={contact.userId} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <StatusBadge status={contact.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(contact.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewContact(contact._id)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => changePage(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  pagination.currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Previous
              </button>
              <button
                onClick={() => changePage(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  pagination.currentPage === pagination.totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {contacts.length > 0
                      ? (pagination.currentPage - 1) * pagination.limit + 1
                      : 0}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      pagination.currentPage * pagination.limit,
                      pagination.total
                    )}
                  </span>{" "}
                  of <span className="font-medium">{pagination.total}</span>{" "}
                  results
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => changePage(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      pagination.currentPage === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft size={18} />
                  </button>

                  {/* Page numbers */}
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      let pageNumber;
                      if (pagination.totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (
                        pagination.currentPage >=
                        pagination.totalPages - 2
                      ) {
                        pageNumber = pagination.totalPages - 4 + i;
                      } else {
                        pageNumber = pagination.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => changePage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagination.currentPage === pageNumber
                              ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => changePage(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      pagination.currentPage === pagination.totalPages
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight size={18} />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Details Modal */}
      {selectedContact && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-2xl">
              {isContactLoading ? (
                <div className="bg-white p-8 flex justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              ) : (
                <>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Message Status
                          </h3>
                          <div className="flex gap-2">
                            <UserTypeBadge userId={selectedContact.userId} />
                            <StatusBadge status={selectedContact.status} />
                          </div>
                        </div>

                        {isEditMode ? (
                          <div className="mt-4">
                            <div className="mb-4">
                              <label
                                htmlFor="status"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Status
                              </label>
                              <select
                                id="status"
                                name="status"
                                value={editForm.status}
                                onChange={handleEditFormChange}
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              >
                                {statusOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option
                                      .split("-")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1)
                                      )
                                      .join(" ")}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="mb-4">
                              <label
                                htmlFor="adminNotes"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Admin Notes
                              </label>
                              <textarea
                                id="adminNotes"
                                name="adminNotes"
                                rows="3"
                                value={editForm.adminNotes}
                                onChange={handleEditFormChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              ></textarea>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Name
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  {selectedContact.userId?.fullName ||
                                    selectedContact.name ||
                                    "N/A"}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Email
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  {selectedContact.userId?.email ||
                                    selectedContact.email ||
                                    "N/A"}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Mobile
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  {selectedContact.userId?.mobileNo ||
                                    selectedContact.mobile ||
                                    "N/A"}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Priority
                                </h4>
                                <p className="mt-1 text-sm text-gray-900 capitalize">
                                  {selectedContact.priority || "medium"}
                                </p>
                              </div>
                            </div>

                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                Subject
                              </h4>
                              <p className="mt-1 text-sm font-medium text-gray-900">
                                {selectedContact.subject}
                              </p>
                            </div>

                            {/* Message History */}
                            <div className="border-t border-gray-200 pt-4 mt-4">
                              {selectedContact.replies &&
                                selectedContact.replies.length > 0 ? (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-500 mb-4">
                                    Conversation History
                                  </h4>
                                  <div className="space-y-4">
                                    {/* Original user message - left aligned with blue background */}
                                    <div className="flex justify-start">
                                      <div className="max-w-[80%] bg-blue-50 p-3 rounded-lg rounded-bl-none">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs font-medium text-blue-600">
                                            User
                                          </span>
                                          <span className="text-xs text-blue-500">
                                            {formatDate(
                                              selectedContact.createdAt
                                            )}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-700 whitespace-pre-line">
                                          {selectedContact.message}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Replies - alternate between user and admin */}
                                    {selectedContact.replies.map(
                                      (reply, index) => (
                                        <div
                                          key={index}
                                          className={`flex ${
                                            reply.sentBy === "admin"
                                              ? "justify-end"
                                              : "justify-start"
                                            }`}
                                        >
                                          <div
                                            className={`max-w-[80%] p-3 rounded-lg ${
                                              reply.sentBy === "admin"
                                                ? "bg-gray-100 rounded-br-none"
                                                : "bg-blue-50 rounded-bl-none"
                                              }`}
                                          >
                                            <div className="flex items-center justify-between mb-1">
                                              <span
                                                className={`text-xs font-medium ${
                                                  reply.sentBy === "admin"
                                                    ? "text-gray-600"
                                                    : "text-blue-600"
                                                  }`}
                                              >
                                                {reply.sentBy === "admin"
                                                  ? "Admin"
                                                  : "User"}
                                              </span>
                                              <span
                                                className={`text-xs ${
                                                  reply.sentBy === "admin"
                                                    ? "text-gray-500"
                                                    : "text-blue-500"
                                                  }`}
                                              >
                                                {formatDate(reply.sentAt)}
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-700 whitespace-pre-line">
                                              {reply.message}
                                            </p>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-blue-50 p-3 rounded-lg">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-blue-600">
                                      User
                                    </span>
                                    <span className="text-xs text-blue-500">
                                      {formatDate(selectedContact.createdAt)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700 whitespace-pre-line">
                                    {selectedContact.message}
                                  </p>
                                </div>
                              )}
                            </div>

                            {selectedContact.adminNotes && (
                              <div className="bg-yellow-50 p-3 rounded-lg">
                                <h4 className="text-xs font-medium text-yellow-600 mb-1">
                                  Internal Notes
                                </h4>
                                <p className="text-sm text-gray-700 whitespace-pre-line">
                                  {selectedContact.adminNotes}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 w-full">
                    {isEditMode ? (
                      <>
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={handleSaveContact}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              Saving...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <Save size={16} className="mr-2" />
                              Save Changes
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => setIsEditMode(false)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : isReplyMode ? (
                      <ContactReplyForm
                        contact={selectedContact}
                        onReplySuccess={handleReplySuccess}
                        onCancel={() => setIsReplyMode(false)}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={handleReplyToContact}
                        >
                          <MessageSquare size={16} className="mr-2" />
                          Reply
                        </button>
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={handleEditContact}
                        >
                          <Edit size={16} className="mr-2" />
                          Edit Status
                        </button>
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={closeContactDetails}
                        >
                          Close
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <ContactExport
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        currentFilters={filters}
      />
    </div>
  );
}
